/** @file thinBatch: a mesh drawn as a growing list of thin instances (matrix and RGBA), refilled every frame. */
import '@babylonjs/core/Meshes/thinInstanceMesh.js';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';

/**
 * `reset()` starts a frame; `add(rgba)` returns the new instance's matrix offset in `matrices()` (16 floats, written
 * by the caller) after storing its colour; `commit()` sets the count, uploads and hides the mesh when empty.
 */
export type ThinBatch = { mesh: Mesh; reset(): void; add(rgba: readonly number[]): number; matrices(): Float32Array; commit(): void; count(): number };

/** Thin-instance buffers on `mesh` that double when full (the only allocation after the first frames). */
export function thinBatch(mesh: Mesh, capacity = 16): ThinBatch {
  let buf = { m: new Float32Array(16 * capacity), c: new Float32Array(4 * capacity) };
  const bind = (): void => { mesh.thinInstanceSetBuffer('matrix', buf.m, 16, false); mesh.thinInstanceSetBuffer('color', buf.c, 4, false); };
  bind();
  mesh.thinInstanceCount = 0;
  mesh.setEnabled(false);
  let k = 0;
  return {
    mesh,
    reset: () => { k = 0; },
    add: (rgba) => {
      if (16 * (k + 1) > buf.m.length) {
        const m = new Float32Array(buf.m.length * 2); m.set(buf.m);
        const c = new Float32Array(buf.c.length * 2); c.set(buf.c);
        buf = { m, c };
        bind();
      }
      buf.c.set(rgba, 4 * k);
      k++;
      return 16 * (k - 1);
    },
    matrices: () => buf.m,
    commit: () => {
      mesh.setEnabled(k > 0);
      mesh.thinInstanceCount = k;
      if (k > 0) { mesh.thinInstanceBufferUpdated('matrix'); mesh.thinInstanceBufferUpdated('color'); }
    },
    count: () => k,
  };
}
